import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { Anchor, Lock, User, Eye } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { handleAnchorClick, isDemoMode, setIsDemoMode, demoClickCount } = useDemoMode();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Réinitialiser le flag de boot pour forcer le passage par la séquence
      sessionStorage.removeItem('cesm_boot_completed');
      // Désactiver le mode démo si on se connecte vraiment
      setIsDemoMode(false);
      await login(email, password);
      navigate('/boot');
    } catch (err) {
      setError(err.response?.data?.detail || 'ERREUR AUTHENTIFICATION');
    } finally {
      setLoading(false);
    }
  };

  // Gestion du clic sur l'ancre pour le mode démo
  const onAnchorClick = () => {
    const activated = handleAnchorClick();
    if (activated) {
      // Rediriger vers le dashboard en mode démo
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } else if (demoClickCount > 0) {
      // Feedback visuel des clics
      toast.info(`🔑 ${3 - demoClickCount} clic(s) restant(s) pour le mode démo`, {
        duration: 1500
      });
    }
  };

  return (
    <div className="min-h-screen bg-marine-void flex items-center justify-center relative overflow-hidden">
      {/* Background Grid */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Login Container */}
      <div className="relative z-10 w-full max-w-md px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div 
              className="p-4 border border-marine-cyan/30 relative cursor-pointer hover:border-marine-cyan/60 transition-all"
              onClick={onAnchorClick}
              title="Triple-cliquez pour le mode démo"
            >
              <Anchor className="w-12 h-12 text-marine-cyan" strokeWidth={1.5} />
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-marine-cyan" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-marine-cyan" />
              {demoClickCount > 0 && (
                <div className="absolute -bottom-2 -right-2 w-5 h-5 bg-marine-amber rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-black">{demoClickCount}</span>
                </div>
              )}
            </div>
          </div>
          <h1 className="font-heading text-marine-cyan text-2xl tracking-widest mb-2 text-glow-cyan">
            CESM
          </h1>
          <p className="font-heading text-marine-cyan/70 text-xs tracking-wider mb-1">
            CENTRE D'ÉTUDES STRATÉGIQUES DE LA MARINE
          </p>
          <p className="font-mono text-white/70 text-xs tracking-wider mb-4">
            SYSTÈME OSINT // ACCÈS RESTREINT
          </p>
          
          {/* Prototype Warning */}
          <div className="mt-4 p-3 border-2 border-red-500/50 bg-red-900/20 rounded">
            <p className="font-mono text-red-400 text-xs tracking-wider animate-pulse font-bold text-center">
              ⚠ PROTOTYPE / PREUVE DE CONCEPT ⚠
            </p>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="tech-border bg-marine-card/50 p-6">
            <div className="text-center mb-8">
              <span className="font-heading text-marine-cyan tracking-widest text-sm animate-flicker">
                LOGIN MOT DE PASSE
              </span>
            </div>

            {/* Email Input */}
            <div className="mb-6">
              <label className="block font-mono text-white/70 text-xs tracking-wider mb-2 uppercase">
                Identifiant
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-marine-slate" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-3 pl-10 pr-4 focus:border-marine-cyan focus:outline-none transition-colors"
                  placeholder="identifiant@intradef.gouv.fr"
                  data-testid="login-email-input"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="mb-6">
              <label className="block font-mono text-white/70 text-xs tracking-wider mb-2 uppercase">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-marine-slate" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-3 pl-10 pr-4 focus:border-marine-cyan focus:outline-none transition-colors"
                  placeholder="••••••••••••"
                  data-testid="login-password-input"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 border-l-4 border-marine-red bg-marine-red/10">
                <p className="font-mono text-marine-red text-xs" data-testid="login-error">
                  [ERREUR] {error}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 font-heading text-sm tracking-widest uppercase bg-marine-cyan/10 border border-marine-cyan text-marine-cyan hover:bg-marine-cyan hover:text-black transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-pulse">[</span>
                  AUTHENTIFICATION
                  <span className="animate-pulse">]</span>
                </span>
              ) : (
                'CONNEXION SÉCURISÉE'
              )}
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-8 space-y-3">
          <p className="font-mono text-white/40 text-[10px] tracking-wider">
            CENTRE D'ÉTUDES STRATÉGIQUES DE LA MARINE // MINISTÈRE DES ARMÉES
          </p>
          <p className="font-mono text-marine-cyan/50 text-[10px]">
            CESM OSINT SYSTEM v3.1 | Copyright (C) 2026 - LPP Logiciel CDT (R) Jean-Baptiste Giraud | Conseil Strategique: COL (R) Thierry Assonion
          </p>
          <div className="max-w-lg mx-auto px-4">
            <p className="font-mono text-marine-amber/70 text-[10px] leading-relaxed">
              NOTE: L'analyse du brief utilise des services IA externes. Les résultats OSINT et l'analyse finale sont traités localement.
            </p>
          </div>
          <div className="max-w-lg mx-auto px-4">
            <p className="font-mono text-marine-red/70 text-[9px] leading-relaxed">
              AVERTISSEMENT: Accès réservé au personnel autorisé. Toute tentative d'accès non autorisé
              est susceptible de poursuites.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
